/**
 * RespaAcknowledgment — Agent-facing RESPA disclosure acknowledgment page.
 *
 * Route: /lender/respa-acknowledge?token=X
 *
 * Shown to agents during onboarding when invited by a lender sponsor.
 * Agent must read the 4 pillars, check the acknowledgment box, type
 * their full name, and submit before their account is activated.
 *
 * Per KairosIQ Lender Seat Licensing Policy v1.0 (KOPS-533).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt,
  faCheckCircle,
  faSpinner,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { colors, fonts, FONT_LINK_HREF } from '../broker-intelligence/shared/colors';
import axiosInstance from '../../helpers/api';

const FOUR_PILLARS = [
  {
    id: 'software_only',
    title: '1. Software Access Only',
    description: 'Your access to the KairosIQ AISearchPortal is provided as a software license. KairosIQ provides software access only — not marketing services, lead generation services, settlement services, or any other thing of value on behalf of the sponsoring entity.',
    icon: '💻',
  },
  {
    id: 'no_pass_through',
    title: '2. No Payments or Pass-Throughs',
    description: 'You will not receive any payments, fees, rebates, splits, commissions, or other monetary consideration from the sponsoring entity through KairosIQ\'s platform or through any KairosIQ-administered arrangement. You are an end user of licensed software.',
    icon: '🚫',
  },
  {
    id: 'no_referral_obligations',
    title: '3. No Referral Obligations',
    description: 'You are under NO obligation, expectation, or encouragement to refer mortgage business — or any other settlement service business — back to the sponsoring entity. You are free to refer to any lender, title company, or settlement service provider you choose, on any transaction, at any time.',
    icon: '🔓',
  },
  {
    id: 'independent_decisions',
    title: '4. Independent Business Decisions',
    description: 'Nothing in the platform, in KairosIQ\'s communications to you, or in the terms of your platform access shall imply, suggest, or encourage reciprocal referrals to the sponsoring entity.',
    icon: '🤝',
  },
  {
    id: 'data_privacy',
    title: '5. Your Data Privacy',
    description: 'Your sponsor partner does NOT have access to your emails, contacts, CRM data, personal communications, or any account details. The only data that can be shared is your MLS production history (closed transactions and sales volume from public MLS records) — and only if you explicitly opt in. You may also opt in to AI Coaching, which allows your sponsor to use AI-powered coaching tools based on your production data. You can revoke either permission at any time from your account settings.',
    icon: '🔒',
  },
];

const RespaAcknowledgment: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [acknowledged, setAcknowledged] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [shareProduction, setShareProduction] = useState(false);
  const [allowCoaching, setAllowCoaching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [sponsorName, setSponsorName] = useState('your sponsoring organization');

  // Load sponsor info from token
  useEffect(() => {
    if (token) {
      axiosInstance.get(`/lender/respa-agreement?token=${token}`)
        .then(res => {
          if (res.data.sponsorName) setSponsorName(res.data.sponsorName);
        })
        .catch(() => {});
    }
  }, [token]);

  const handleSubmit = useCallback(async () => {
    if (!acknowledged) {
      setError('You must check the acknowledgment box');
      return;
    }
    if (!typedName.trim() || typedName.trim().split(' ').length < 2) {
      setError('Please type your full name (first and last)');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const res = await axiosInstance.post('/lender/respa-acknowledge', {
        token,
        agentTypedName: typedName.trim(),
        shareProduction,
        allowCoaching,
      });
      if (res.data.success) {
        setSuccess(true);
      } else {
        setError(res.data.error || 'Failed to submit acknowledgment');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [acknowledged, typedName, token]);

  if (success) {
    return (
      <div style={{
        minHeight: '100vh', background: colors.navy, display: 'flex',
        alignItems: 'center', justifyContent: 'center', fontFamily: fonts.body,
      }}>
        <link href={FONT_LINK_HREF} rel="stylesheet" />
        <div style={{
          background: '#fff', borderRadius: 16, padding: 48, maxWidth: 500,
          textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}>
          <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: 64, color: colors.green, marginBottom: 24 }} />
          <h2 style={{ fontFamily: fonts.display, fontSize: 32, color: colors.navy, margin: '0 0 12px' }}>
            Acknowledgment Complete
          </h2>
          <p style={{ color: colors.muted, fontSize: 14, lineHeight: 1.6 }}>
            Thank you for acknowledging the software access terms. Your account is now active.
            You can start using the AISearchPortal immediately.
          </p>
          <button
            onClick={() => navigate('/agent-portal/dashboard')}
            style={{
              marginTop: 24, background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
              border: 'none', borderRadius: 8, padding: '12px 32px',
              color: colors.navy, fontWeight: 700, fontSize: 14, cursor: 'pointer',
              fontFamily: fonts.body,
            }}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh', background: colors.navy, display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: 24,
      fontFamily: fonts.body,
    }}>
      <link href={FONT_LINK_HREF} rel="stylesheet" />
      <div style={{
        background: '#fff', borderRadius: 16, padding: '40px 48px', maxWidth: 640,
        width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <FontAwesomeIcon icon={faShieldAlt} style={{ fontSize: 28, color: colors.navy }} />
          </div>
          <h1 style={{ fontFamily: fonts.display, fontSize: 32, color: colors.navy, margin: '0 0 8px' }}>
            Software Access Acknowledgment
          </h1>
          <p style={{ color: colors.muted, fontSize: 14, margin: 0, lineHeight: 1.5 }}>
            Your access to KairosIQ AISearchPortal is sponsored by <strong style={{ color: colors.navy }}>{sponsorName}</strong>.
            Please review and acknowledge the following terms before accessing the platform.
          </p>
        </div>

        {/* Four Pillars */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 32 }}>
          {FOUR_PILLARS.map(pillar => (
            <div key={pillar.id} style={{
              background: colors.offBg, borderRadius: 10, padding: '16px 20px',
              border: `1px solid ${colors.border}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <span style={{ fontSize: 24, lineHeight: 1 }}>{pillar.icon}</span>
                <div>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: colors.navy, margin: '0 0 6px' }}>
                    {pillar.title}
                  </h3>
                  <p style={{ fontSize: 13, color: '#555', lineHeight: 1.6, margin: 0 }}>
                    {pillar.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Acknowledgment form */}
        <div style={{
          background: colors.navy2, borderRadius: 10, padding: 24,
          border: `1px solid ${colors.navy3}`,
        }}>
          {/* Checkbox */}
          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
            marginBottom: 16,
          }}>
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={e => { setAcknowledged(e.target.checked); setError(''); }}
              style={{ marginTop: 3, width: 18, height: 18, accentColor: colors.gold }}
            />
            <span style={{ fontSize: 13, color: colors.white, lineHeight: 1.5 }}>
              I have read, understood, and agree to the terms above. I acknowledge that my access to
              KairosIQ AISearchPortal is a software license only, and that I have <strong>no referral
              obligations</strong> to the sponsoring entity.
            </span>
          </label>

          {/* Data sharing opt-ins */}
          <div style={{ marginBottom: 16, borderTop: `1px solid ${colors.navy3}`, paddingTop: 16 }}>
            <div style={{ fontSize: 12, color: colors.gold2, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
              Optional Data Sharing (you can change these anytime)
            </div>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 12 }}>
              <input type="checkbox" checked={shareProduction} onChange={e => setShareProduction(e.target.checked)}
                style={{ marginTop: 3, width: 18, height: 18, accentColor: colors.gold }} />
              <span style={{ fontSize: 13, color: colors.white, lineHeight: 1.5 }}>
                <strong>Share MLS Production Data</strong> — Allow your sponsor partner to view your MLS production history
                (closed transactions and sales volume from public MLS records). This does NOT include your emails, contacts, CRM data, or personal communications.
              </span>
            </label>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={allowCoaching} onChange={e => setAllowCoaching(e.target.checked)}
                style={{ marginTop: 3, width: 18, height: 18, accentColor: colors.gold }} />
              <span style={{ fontSize: 13, color: colors.white, lineHeight: 1.5 }}>
                <strong>Enable AI Coaching</strong> — Allow your sponsor partner to use AI-powered coaching tools
                to help you grow your business based on your production data. Your sponsor can generate coaching insights but cannot access your account directly.
              </span>
            </label>
          </div>

          {/* Typed signature */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: colors.muted, display: 'block', marginBottom: 4 }}>
              Type your full legal name (electronic signature)
            </label>
            <input
              type="text"
              value={typedName}
              onChange={e => { setTypedName(e.target.value); setError(''); }}
              placeholder="First Last"
              style={{
                width: '100%', background: colors.navy3, border: 'none',
                borderRadius: 6, padding: '10px 14px', color: colors.white,
                fontSize: 16, fontFamily: "'Caveat', cursive, " + fonts.body,
                fontStyle: 'italic', outline: 'none',
              }}
            />
          </div>

          {error && (
            <div style={{ color: colors.red, fontSize: 12, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={submitting || !acknowledged || !typedName.trim()}
            style={{
              width: '100%',
              background: (acknowledged && typedName.trim())
                ? `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`
                : colors.navy3,
              border: 'none', borderRadius: 8, padding: '12px',
              color: (acknowledged && typedName.trim()) ? colors.navy : colors.muted,
              fontWeight: 700, fontSize: 14, cursor: (acknowledged && typedName.trim()) ? 'pointer' : 'default',
              fontFamily: fonts.body, transition: 'all 0.2s',
            }}
          >
            {submitting
              ? <><FontAwesomeIcon icon={faSpinner} spin /> Submitting...</>
              : 'Acknowledge & Activate Account'
            }
          </button>
        </div>

        <p style={{ textAlign: 'center', fontSize: 11, color: colors.muted, marginTop: 16 }}>
          KairosIQ AI, Inc. — Software Access Acknowledgment — Agreement Version 1.0
        </p>
      </div>
    </div>
  );
};

export default RespaAcknowledgment;
